'use client'

import React, { ReactNode, useEffect, useState } from 'react'
import { useWikiContext } from '@/context/wikiContext'
import { MaxWidthGlobal } from '@/ui/layout/Gutter/new/MaxWidthGlobal'
import { Icon } from '@/ui/atoms/Icon'
import { SIDENAV_COLLAPSED_WIDTH, SIDENAV_EXPANDED_WIDTH } from '@/ui/layout/Gutter/new/const'
import { ContentGutter } from '@/ui/layout/Gutter/new/ContentGutter'
import { sendGAEvent } from '@next/third-parties/google'
import { trackEvent } from '@/lib/analytics/posthog'

interface WikiGutterProps {
  sideNavTitle: string
  children: ReactNode | ReactNode[]
  sideNav?: ReactNode
  fullWidthHero?: ReactNode
}

export const WikiGutter: React.FC<WikiGutterProps> = ({
  fullWidthHero,
  sideNavTitle,
  sideNav,
  children,
}: WikiGutterProps) => {
  const [sideNavIsCollapsed, setSideNavIsCollapsed] = useState(false)
  const { breadcrumbs, onPageNav } = useWikiContext()

  return (
    <MaxWidthGlobal>
      <div className="w-full pb-5">
        <div className="max-w-[1920px] w-full mx-auto px-mobile-grid-gutter lg:px-grid-gutter">
          <div className="relative flex flex-col lg:flex-row w-full gap-2 lg:gap-5">
            {sideNav && (
              <SideNavPanel
                title={sideNavTitle}
                isCollapsed={sideNavIsCollapsed}
                onToggleCollapse={() => {
                  sendGAEvent('event', 'sideNavToggled')
                  trackEvent('sideNavToggled')
                  setSideNavIsCollapsed((prev) => !prev)
                }}
              >
                {sideNav}
              </SideNavPanel>
            )}
            <div className="relative flex-1 min-w-0">
              <div className="flex items-center gap-2 sticky top-[80px] lg:top-[96px] z-10 bg-white bg-opacity-80 backdrop-blur">
                {sideNav && (
                  <div className="invisible lg:hidden h-8 flex-shrink-0 border border-border-subtle rounded-md bg-white bg-opacity-80 backdrop-blur overflow-hidden">
                    <button
                      className="h-full px-2 flex items-center justify-center"
                      onClick={() => setSideNavIsCollapsed(false)}
                    >
                      <Icon name="sidebar-unfold-line" className="text-text-subtle size-4" />
                    </button>
                  </div>
                )}
                <div className="flex-1 min-w-0 overflow-x-auto scrollbar-hide">{breadcrumbs}</div>
              </div>
              {fullWidthHero && <div className="w-full bg-blue-200 h-160">{fullWidthHero}</div>}
              <ContentGutter onPageNav={onPageNav} className="lg:pt-20 pt-5">
                {children}
              </ContentGutter>
            </div>
          </div>
        </div>
      </div>
    </MaxWidthGlobal>
  )
}

export const SideNavPanel: React.FC<{
  children: ReactNode
  title: string
  /** optional: the title links here (the workflows hub on its stage and equipment pages) */
  titleHref?: string
  isCollapsed: boolean
  onToggleCollapse: () => void
}> = ({ children, title, titleHref, isCollapsed, onToggleCollapse }) => {
  const [isMobile, setIsMobile] = React.useState(false)

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024)
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)

    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  React.useEffect(() => {
    if (isMobile && !isCollapsed) {
      onToggleCollapse()
    }
  }, [isMobile])

  return (
    <div
      className={`z-40 top-[80px] transition-all duration-300 ease-in-out ${
        isCollapsed
          ? 'h-8 sticky bg-white bg-opacity-80 backdrop-blur'
          : 'h-[calc(100vh-32px-80px)] fixed inset-x-5 bg-bg-subtle-blue'
      } lg:sticky lg:top-[96px] ${
        isCollapsed ? 'lg:h-8' : 'lg:h-[calc(100vh-116px)]'
      } border-[1px] border-border-subtle rounded-md ${
        isCollapsed ? SIDENAV_COLLAPSED_WIDTH : SIDENAV_EXPANDED_WIDTH
      } flex-shrink-0 mb-2 lg:mb-0 overflow-hidden`}
    >
      <div className="h-8 relative flex justify-start items-center">
        <div
          className={`flex flex-row justify-start items-start w-[calc(100%-32px)] ps-3 transition-opacity duration-200 ${
            isCollapsed ? 'opacity-0' : 'opacity-100'
          }`}
        >
          {titleHref ? (
            <a href={titleHref} className="text-label-XS-semibold text-text-faded hover:text-text-accent hover:underline underline-offset-4">
              {title}
            </a>
          ) : (
            <span className="text-label-XS-semibold text-text-faded">{title}</span>
          )}
        </div>
        <button
          className="absolute top-0 right-0 h-8 w-8 hover:!bg-neutral-95 rounded-sm flex items-center justify-center group"
          onClick={onToggleCollapse}
        >
          <Icon
            name={!isCollapsed ? 'sidebar-fold-line' : 'sidebar-unfold-line'}
            className="text-text-subtle size-4 transition-transform duration-300 ease-in-out group-hover:scale-110"
          />
        </button>
      </div>
      <div
        className={`p-3 h-[calc(100%-32px)] overflow-y-auto overscroll-contain transition-all duration-150 ease-in-out ${
          isCollapsed ? 'opacity-0 transform -translate-x-2' : 'opacity-100 transform translate-x-0'
        }`}
      >
        {children}
      </div>
    </div>
  )
}
